import React, { useEffect, useState } from 'react'
import {
  Box, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from '@mui/material'
import { NewLayout } from '../../../components/newLayout/NewLayout'
import { fetchRequest } from '../../../services/http.service'
import { DocumentFile, MgmtFeeRun } from '../api/types'
import { StatementButtons } from '../components/StatementButtons'
import { InfoHint } from '../components/InfoHint'
import { PageHeader } from '../components/PageHeader'

/**
 * Distributorské rozhraní „Management fee" (F7-G). Distributor vidí JEN své reporty —
 * BE endpoint /api/funds/my/mgmt-fee/runs je server-side scopovaný na jeho jmenné role,
 * vrací pouze jeho klienty (nikdy cizí, i souhrn NAV fondů je odfiltrován).
 */
export function DistributorMgmtFee() {
  const [runs, setRuns] = useState<MgmtFeeRun[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    fetchRequest
      .get<any[]>('funds/my/mgmt-fee/runs')
      .then((r) => {
        if (!alive) return
        setRuns((r.data || []).map(numify))
      })
      .catch((e) => {
        if (alive) setError(e && e.response && e.response.status === 403
          ? 'Nemáte přiřazený profil obsluhy pro Management fee.'
          : 'Reporty se nepodařilo načíst.')
      })
    return () => {
      alive = false
    }
  }, [])

  const fmt = (n: number) =>
    new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 2 }).format(n)

  return (
    <NewLayout pageName="Management fee">
      <Box>
        <PageHeader
          title="Management fee"
          subtitle="Přehled vašich měsíčních reportů"
          info={
            <InfoHint
              title="Jak se report počítá"
              lines={[
                'Vidíte pouze své klienty (klienty ve vaší obsluze) — data jsou filtrovaná na serveru.',
                'Základ klienta = jeho jednotky × cena ke konci měsíce × 1,47 % p.a. / 12.',
                'Vklad se započítává od měsíce následujícího po vkladu.',
                'Vaše provize = základ klienta × váš nasmlouvaný podíl.',
              ]}
            />
          }
        />
        {error && <Typography sx={{ p: 2, color: 'error.main' }}>{error}</Typography>}
        {!error && !runs && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {runs && runs.length === 0 && <Typography sx={{ p: 2 }}>Zatím žádné reporty.</Typography>}
        {runs && runs.map((run) => (
          <Paper key={run.id} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">
                {run.period}
                <Chip size="small" sx={{ ml: 1 }} label={`${run.lines.length} klientů`} />
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontWeight: 600 }}>
                  {run.totalCommission != null ? 'Provize ' + fmt(run.totalCommission) : fmt(run.totalFee)}
                </Typography>
                <StatementButtons
                  title="Vyúčtování provize"
                  load={(format) =>
                    fetchRequest
                      .get<any>('funds/my/mgmt-fee/runs/' + run.id + '/statement?format=' + format)
                      .then((r) => r.data as DocumentFile)
                  }
                  onError={setError}
                />
              </Box>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Klient</TableCell>
                  <TableCell align="right">Hodnota majetku</TableCell>
                  <TableCell align="right">Sazba p.a.</TableCell>
                  <TableCell align="right">Stržené fee</TableCell>
                  <TableCell align="right">Váš podíl</TableCell>
                  <TableCell align="right">Vaše provize</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {run.lines.map((l) => (
                  <TableRow key={l.clientId}>
                    <TableCell>{l.clientName}</TableCell>
                    <TableCell align="right">{fmt(l.holdingValue)}</TableCell>
                    <TableCell align="right">{(l.ratePa * 100).toFixed(2)} %</TableCell>
                    <TableCell align="right">{fmt(l.fee)}</TableCell>
                    <TableCell align="right">
                      {l.commissionRate != null ? (l.commissionRate * 100).toFixed(0) + ' %' : '—'}
                    </TableCell>
                    <TableCell align="right">{fmt(l.commission ?? 0)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        ))}
      </Box>
    </NewLayout>
  )
}

/** BE posílá čísla jako string — převod pro zobrazení (scoped report = jen vlastní klienti). */
function numify(run: any): MgmtFeeRun {
  return {
    ...run,
    totalFee: Number(run.totalFee),
    totalCommission: run.totalCommission != null ? Number(run.totalCommission) : null,
    lines: (run.lines || []).map((l: any) => ({
      ...l,
      holdingValue: Number(l.holdingValue),
      ratePa: Number(l.ratePa),
      fee: Number(l.fee),
      commissionRate: l.commissionRate != null ? Number(l.commissionRate) : null,
      commission: l.commission != null ? Number(l.commission) : null,
    })),
    byDistributor: (run.byDistributor || []).map((d: any) => ({
      ...d,
      totalHoldingValue: Number(d.totalHoldingValue),
      totalFee: Number(d.totalFee),
    })),
    fundReductions: run.fundReductions || [],
    // Sekce kontroly (AI per distributor + celofondové souhrny) je celofondová —
    // do distributorského pohledu nepatří, zahazujeme ji hned při načtení.
    check: undefined,
  }
}
